import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ShieldCheck, Truck, Lock, Sparkles, RefreshCcw, Package, CheckCircle2, Tag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const initialAddress = { name: '', phone: '', email: '', line1: '', line2: '', city: '', state: '', pincode: '' };

// Module-scope so identity is stable across renders (prevents input focus loss)
const Field = ({ label, k, value, onChange, full, type = 'text', placeholder }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className="text-sm font-medium text-stone-700 mb-1.5 block">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={`checkout-${k}`}
      className="w-full bg-stone-50 border border-stone-200 text-stone-800 rounded-lg h-12 px-4 text-sm placeholder:text-stone-400 focus:bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:outline-none transition-colors"
    />
  </div>
);

const METHOD_CHIPS = ['UPI', 'Cards', 'Netbanking', 'Wallets'];
const TRUST_BADGES = [
  { icon: Lock, label: '256-bit SSL Secure' },
  { icon: ShieldCheck, label: 'Secured by Razorpay' },
  { icon: Sparkles, label: '100% Authentic' },
  { icon: RefreshCcw, label: '7-Day Easy Returns' },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [address, setAddress] = useState(initialAddress);
  const [placing, setPlacing] = useState(false);
  const [config, setConfig] = useState({ razorpay_enabled: true });
  const [quote, setQuote] = useState({ subtotal: cartTotal, shipping: 0, discount: 0, total: cartTotal, weight: 0, discount_code: null });
  const [discountInput, setDiscountInput] = useState('');
  const [appliedCode, setAppliedCode] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/orders/config`).then((r) => r.json()).then(setConfig).catch(() => {});
    loadRazorpay(); // preload the gateway so the payment window opens instantly
  }, []);

  const fetchQuote = React.useCallback(async (code) => {
    if (cartItems.length === 0) return;
    setQuoting(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/quote`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems.map((i) => ({ id: i.id, quantity: i.quantity })), discount_code: code || null }),
      });
      const data = await res.json();
      setQuote(data);
      if (code) {
        if (data.discount_error) { toast.error(data.discount_error); setAppliedCode(null); }
        else if (data.discount_code) { setAppliedCode(data.discount_code); toast.success(`Code ${data.discount_code} applied!`); }
      }
    } catch (e) { /* ignore */ } finally { setQuoting(false); }
  }, [cartItems]);

  useEffect(() => { fetchQuote(appliedCode); }, [fetchQuote]);

  // Repeat-customer auto-fill: enter phone -> pre-fill saved details
  const prefilledRef = React.useRef('');
  useEffect(() => {
    const digits = address.phone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10 || prefilledRef.current === digits) return;
    let cancelled = false;
    fetch(`${API_URL}/api/orders/customer/lookup?phone=${digits}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d.found) return;
        prefilledRef.current = digits;
        setAddress((prev) => ({
          ...prev,
          name: prev.name || d.name || '',
          email: prev.email || d.email || '',
          ...(d.address ? {
            line1: prev.line1 || d.address.line1 || '',
            line2: prev.line2 || d.address.line2 || '',
            city: prev.city || d.address.city || '',
            state: prev.state || d.address.state || '',
            pincode: prev.pincode || d.address.pincode || '',
          } : {}),
        }));
        setAutoFilled(true);
        toast.success('Welcome back! We securely loaded your saved address.');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [address.phone]);

  // Abandoned-cart capture: once a valid email is entered, save the cart
  useEffect(() => {
    const email = (address.email || '').trim();
    if (!/^\S+@\S+\.\S+$/.test(email) || cartItems.length === 0) return;
    const t = setTimeout(() => {
      fetch(`${API_URL}/api/orders/save-cart`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, phone: address.phone, name: address.name,
          items: cartItems.map((i) => ({ id: i.id, quantity: i.quantity })) }),
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [address.email, address.phone, address.name, cartItems]);

  useEffect(() => {
    if (cartItems.length === 0 && !placing) navigate('/products');
  }, [cartItems, placing, navigate]);

  const set = (k, v) => setAddress((a) => ({ ...a, [k]: v }));
  const shipping = quote.shipping || 0;
  const grandTotal = quote.total || cartTotal;

  const validate = () => {
    const req = ['name', 'phone', 'line1', 'city', 'state', 'pincode'];
    for (const f of req) if (!address[f]?.trim()) { toast.error(`Please fill ${f}`); return false; }
    if (!/^\d{10}$/.test(address.phone.replace(/\D/g, '').slice(-10))) { toast.error('Enter a valid 10-digit phone'); return false; }
    if (!/^\d{6}$/.test(address.pincode)) { toast.error('Enter a valid 6-digit pincode'); return false; }
    return true;
  };

  const payload = () => ({ items: cartItems.map((i) => ({ id: i.id, quantity: i.quantity })), address, discount_code: appliedCode || null });

  const finishOrder = (data) => {
    clearCart();
    navigate(`/order-success/${data.order_id}`);
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error('Could not load payment gateway');
      const res = await fetch(`${API_URL}/api/orders/create-razorpay`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload()),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Could not start payment');
      const data = await res.json();
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'Astro Vastu Remedies',
        description: `Order ${data.order_number}`,
        order_id: data.razorpay_order_id,
        prefill: { name: address.name, email: address.email, contact: address.phone },
        retry: { enabled: true },
        theme: { color: '#F59E0B' },
        handler: async (response) => {
          try {
            const vr = await fetch(`${API_URL}/api/orders/verify`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                order_id: data.order_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            if (!vr.ok) throw new Error('Payment verification failed');
            finishOrder(await vr.json());
          } catch (e) {
            toast.error(e.message);
            setPlacing(false);
          }
        },
        modal: { ondismiss: () => setPlacing(false), confirm_close: true },
      });
      rzp.on('payment.failed', () => { toast.error('Payment failed. Please try again.'); setPlacing(false); });
      rzp.open();
    } catch (e) {
      toast.error(e.message);
      setPlacing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] py-10 lg:py-16" data-testid="checkout-page">
      <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8 lg:mb-10">
          <div className="inline-flex items-center gap-2 text-amber-700 bg-amber-100/70 border border-amber-200 rounded-full px-3 py-1 text-xs font-medium mb-3">
            <Lock className="w-3.5 h-3.5" /> Secure Checkout
          </div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold tracking-tight text-stone-900">Complete your order</h1>
          <p className="text-stone-500 mt-2 text-sm">Trusted by thousands of seekers across India — your details are encrypted &amp; safe.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left: Address + payment */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-7 space-y-6"
          >
            <AnimatePresence>
              {autoFilled && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: -8 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3"
                  aria-live="polite"
                  data-testid="autofill-banner"
                >
                  <Sparkles className="w-5 h-5 text-emerald-500 shrink-0" />
                  <p className="text-sm font-medium">Welcome back! We securely loaded your saved address.</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Shipping */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-amber-500 text-amber-950 font-bold flex items-center justify-center text-sm">1</span>
                <h2 className="font-serif text-xl font-bold tracking-tight text-stone-900">Shipping Details</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full Name *" k="name" value={address.name} onChange={(e) => set('name', e.target.value)} placeholder="Your name" />
                <Field label="Phone *" k="phone" value={address.phone} onChange={(e) => set('phone', e.target.value)} placeholder="10-digit mobile" />
                <Field label="Email (for order updates)" k="email" full value={address.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" />
                <Field label="Address Line 1 *" k="line1" full value={address.line1} onChange={(e) => set('line1', e.target.value)} placeholder="House no., street" />
                <Field label="Address Line 2" k="line2" full value={address.line2} onChange={(e) => set('line2', e.target.value)} placeholder="Area, landmark (optional)" />
                <Field label="City *" k="city" value={address.city} onChange={(e) => set('city', e.target.value)} placeholder="City" />
                <Field label="State *" k="state" value={address.state} onChange={(e) => set('state', e.target.value)} placeholder="State" />
                <Field label="Pincode *" k="pincode" value={address.pincode} onChange={(e) => set('pincode', e.target.value)} placeholder="6-digit PIN" />
              </div>
            </div>

            {/* Payment */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-amber-500 text-amber-950 font-bold flex items-center justify-center text-sm">2</span>
                <h2 className="font-serif text-xl font-bold tracking-tight text-stone-900">Payment</h2>
              </div>
              <div className="border-2 border-amber-500 bg-amber-50/50 rounded-xl p-5" data-testid="pay-razorpay">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-5 h-5 text-amber-950" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-stone-900">Pay Securely Online</p>
                      <CheckCircle2 className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">Instant confirmation · Powered by Razorpay</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {METHOD_CHIPS.map((m) => (
                        <span key={m} className="text-xs font-medium text-stone-600 bg-white border border-stone-200 rounded-md px-2.5 py-1">{m}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-stone-400 mt-3 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Your payment is encrypted. We never store your card details.
              </p>
            </div>
          </motion.div>

          {/* Right: Summary */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.12 }}
            className="lg:col-span-5"
          >
            <div className="bg-[#FFF9F2] rounded-2xl border border-amber-100 p-6 md:p-8 lg:sticky lg:top-8">
              <h2 className="font-serif text-xl font-bold tracking-tight text-stone-900 mb-5">Order Summary</h2>
              <div className="space-y-4 max-h-72 overflow-y-auto mb-5 pr-1">
                {cartItems.map((it) => (
                  <div key={it.id} className="flex items-center gap-3" data-testid={`summary-item-${it.id}`}>
                    <div className="relative w-16 h-16 rounded-lg bg-white border border-amber-100 overflow-hidden shrink-0">
                      {it.image ? (
                        <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-amber-300" /></div>
                      )}
                      <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-amber-500 text-amber-950 text-[10px] font-bold flex items-center justify-center border-2 border-[#FFF9F2]">{it.quantity}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-800 line-clamp-2">{it.name}</p>
                    </div>
                    <span className="text-sm font-semibold text-stone-900">{formatINR(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>

              {/* Discount code */}
              <div className="flex gap-2 mb-5">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                    placeholder="Discount code"
                    data-testid="discount-input"
                    className="w-full bg-white border border-stone-200 rounded-lg pl-9 pr-3 h-11 text-sm uppercase placeholder:normal-case focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:outline-none transition-colors"
                  />
                </div>
                <button
                  onClick={() => fetchQuote(discountInput.trim())}
                  disabled={!discountInput.trim() || quoting}
                  data-testid="apply-discount-btn"
                  className="px-5 h-11 text-sm bg-white border border-amber-400 text-amber-700 rounded-lg font-semibold hover:bg-amber-50 disabled:opacity-50 transition-colors"
                >
                  Apply
                </button>
              </div>

              <div className="border-t border-amber-100 pt-4 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-stone-500">Subtotal</span><span className="text-stone-800 font-medium" data-testid="summary-subtotal">{formatINR(quote.subtotal)}</span></div>
                <div className="flex justify-between">
                  <span className="text-stone-500 flex items-center gap-1"><Truck className="w-4 h-4 text-stone-400" /> Shipping {quote.weight ? `(${quote.weight} kg)` : ''}</span>
                  <span className="text-stone-800 font-medium" data-testid="summary-shipping">{shipping === 0 ? <span className="text-emerald-600 font-semibold">FREE</span> : formatINR(shipping)}</span>
                </div>
                <AnimatePresence>
                  {quote.discount > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex justify-between text-emerald-600"
                    >
                      <span>Discount ({appliedCode})</span><span data-testid="summary-discount">- {formatINR(quote.discount)}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className="flex justify-between items-baseline font-bold text-lg pt-3 border-t border-amber-100">
                  <span className="text-stone-900">Total</span>
                  <span className="text-amber-600" data-testid="summary-total">{formatINR(grandTotal)}</span>
                </div>
                <p className="text-xs text-stone-400 text-right">Inclusive of all taxes · No hidden charges</p>
              </div>

              <button
                onClick={placeOrder}
                disabled={placing}
                data-testid="place-order-btn"
                className="w-full mt-5 py-4 rounded-xl text-base font-bold bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 shadow-lg shadow-amber-500/20 hover:shadow-xl hover:shadow-amber-500/40 hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {placing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-4 h-4" />}
                {`Pay ${formatINR(grandTotal)}`}
              </button>

              {/* Trust badges */}
              <div className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t border-amber-100">
                {TRUST_BADGES.map((b) => (
                  <div key={b.label} className="flex items-center gap-2 text-xs text-stone-500">
                    <b.icon className="w-4 h-4 text-amber-500 shrink-0" />
                    <span>{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
